import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function sumArray(arr: number[]) {
  return (arr || []).reduce((a, b) => a + b, 0)
}

export function roundElements(arr: number[], sigFigs: number): number[] {
  return (arr || []).map((num) => Number(num.toFixed(sigFigs)))
}

export function debounce<T extends (...args: any[]) => any>(func: T, delay: number) {
  let timeoutId: NodeJS.Timeout;
  return (...args: Parameters<T>): void => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func.apply(null, args);
    }, delay);
  };
};

export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) {
  let lastCall = 0;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: Parameters<T> | null = null;

  return (...args: Parameters<T>) => {
    const now = Date.now();
    
    // Save the latest args
    lastArgs = args;
    
    // If it's been longer than the limit since last call, execute immediately
    if (now - lastCall >= limit) {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      lastCall = now;
      func(...args);
    } else if (!timeoutId) {
      // Otherwise, set up a timeout to run the latest call after the limit expires
      timeoutId = setTimeout(() => {
        lastCall = Date.now();
        timeoutId = null;
        if (lastArgs) {
          func(...lastArgs);
        }
      }, limit - (now - lastCall));
    }
    // If a timeout is already pending, the latest args will be used when it fires
  };
};

// Generate a stable hash from a string
export function stringToHashIndex(str: string, modulus: number): number {
  if (!str) return 0; // Handle undefined or empty string
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash) % modulus;
};

// Extract user-friendly error messages from Relay errors
export function getRelayErrorMessage(error: Error): string {
  const errorObj = error as any;

  // Handle standard Relay network errors
  if (errorObj.message && errorObj.message.startsWith("RelayNetwork:")) {
    // Parse the error message to extract the actual GraphQL error
    const match = errorObj.message.match(/got error\(s\):\s*(.*?)(\n|$)/);
    if (match && match[1]) {
      return match[1].trim();
    }
  }

  // Handle errors with source field (Relay response errors)
  if (errorObj.source && errorObj.source.errors && errorObj.source.errors.length > 0) {
    return errorObj.source.errors
      .map((e: any) => e.message || "Unknown error")
      .join(", ");
  }

  // Fallback to the original error message
  return errorObj.message || "An unexpected error occurred";
};

/**
 * Calculate Levenshtein distance between two strings.
 * Useful for fuzzy string matching and typo tolerance in search.
 */
export function levenshteinDistance(str1: string, str2: string): number {
  const len1 = str1.length;
  const len2 = str2.length;
  const matrix: number[][] = [];

  for (let i = 0; i <= len1; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(matrix[i - 1][j - 1] + 1, matrix[i][j - 1] + 1, matrix[i - 1][j] + 1);
      }
    }
  }

  return matrix[len1][len2];
}